import { Alert, Button, Card, Col, Form, InputNumber, message, Row, Space, Statistic, Switch } from "antd";
import { useWatch } from "antd/es/form/Form";
import React, { useCallback, useEffect, useState } from "react";
import { CustomStrategyConstants } from "utils/constants";
import { formatMoney, getDebtBasedWeeks } from "utils/functions";
import {
  calculateNetCommissionAmount,
  calculateRevisedWeeks,
  calculateWeeklyCommissionAmount,
  calculateWeeklyInitialPayment,
  getCalculationFields,
  getMinimumWeeklyBudget,
  getTotalInitialPayment,
} from "./functions";

const StrategyPanel = ({
  name,
  totalDebt,
  remove,
  activeKey,
  setActiveKey,
  setDisabled,
  extraAmount,
  minWeeklyBudget,
  totalLawsuits,
  hasSplit,
}: {
  name: number;
  totalDebt: number;
  remove: (name: number) => void;
  activeKey: string | string[] | undefined;
  setActiveKey: React.Dispatch<React.SetStateAction<string | string[] | undefined>>;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  extraAmount: number;
  minWeeklyBudget: number;
  totalLawsuits: number;
  hasSplit: boolean;
}) => {
  const form = Form.useFormInstance();

  const weekly_budget: number = useWatch(["strategies", name, "weekly_budget"], form);
  const initial_payment: number = useWatch(["strategies", name, "initial_payment"], form);
  const initial_payment_timeline: number = useWatch(["strategies", name, "initial_payment_timeline"], form);
  const program_part: number = useWatch(["strategies", name, "program_part"], form) ?? 0;
  const success_part: number = useWatch(["strategies", name, "success_part"], form) ?? 0;
  const net_commission: number = useWatch(["strategies", name, "net_commission"], form);
  const commission_timeline: number =
    useWatch(["strategies", name, "commission_timeline"], form) ?? getDebtBasedWeeks(totalDebt).interest_weeks_3;
  const display_timeline: number = useWatch(["strategies", name, "display_timeline"], form);
  const creditor_estimate: number = useWatch(["strategies", name, "creditor_estimate"], form);
  const include_commission_payment: boolean = useWatch(["strategies", name, "include_commission_payment"], form);
  const bias_amount: number = useWatch(["strategies", name, "bias_amount"], form);
  const initial_payment_charge: number = useWatch(["strategies", name, "initial_payment_charge"], form);
  const creditor_estimate_split: number[] = useWatch(["strategies", name, "creditor_estimate_split"], form);

  const weeklyInitialPayment = calculateWeeklyInitialPayment(initial_payment_timeline, initial_payment);

  // const defaultDisplayTimeline = () => {
  //   Math.floor(((totalDebt * (1 + net_commission / 100)) / weekly_budget) * 0.85);
  // };

  const weeklyCommissionPayment = calculateWeeklyCommissionAmount(
    commission_timeline,
    net_commission,
    totalDebt,
    initial_payment_charge,
    weekly_budget,
    bias_amount,
  );

  // const changedMinWeeklyBudget = useCallback(() => {
  //   console.log(initial_payment_timeline);

  //   return getMinimumWeeklyBudget(
  //     initial_payment_timeline,
  //     initial_payment,
  //     weekly_budget,
  //     weeklyCommissionPayment,
  //     totalLawsuits,
  //   );
  // }, [initial_payment_timeline, initial_payment, weekly_budget, weeklyCommissionPayment, totalLawsuits])();

  const changedMinWeeklyBudget = getMinimumWeeklyBudget(
    initial_payment_timeline,
    initial_payment,
    // weekly_budget,
    // weeklyCommissionPayment,
    totalLawsuits,
    {
      commission_timeline,
      initial_payment_charge,
      net_commission,
      totalDebt,
    },
  );

  useEffect(() => {
    const total = (program_part ?? 0) + (success_part ?? 0);

    form.setFieldsValue({
      strategies: {
        [name]: {
          net_commission: total,
        },
      },
    });
  }, [program_part, success_part]);

  useEffect(() => {
    if (
      !commission_timeline ||
      !net_commission ||
      !totalDebt ||
      // !initial_payment_charge ||
      !weekly_budget ||
      !weeklyCommissionPayment ||
      !weeklyInitialPayment
    )
      return;

    if (weeklyCommissionPayment < 0 || weekly_budget < 0) return;

    // console.log("before call", weeklyCommissionPayment, weeklyInitialPayment);

    const { weeks, creditor_estimate, creditor_estimate_split } = calculateRevisedWeeks({
      initial_payment_timeline,
      commission_timeline,
      include_commission_payment,
      totalDebt,
      weekly_budget,
      weeklyCommissionPayment,
      weeklyInitialPayment,
      total_lawsuits: totalLawsuits,
    });

    form.setFieldsValue({
      strategies: { [name]: { weeks, creditor_estimate, creditor_estimate_split } },
    });

    form.validateFields();
  }, [
    initial_payment_timeline,
    commission_timeline,
    include_commission_payment,
    weekly_budget,
    weeklyCommissionPayment,
    weeklyInitialPayment,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      strategies: {
        [name]: {
          initial_payment: getTotalInitialPayment(initial_payment_charge, totalDebt, extraAmount, bias_amount),
        },
      },
    });
  }, [initial_payment_charge, bias_amount]);

  useEffect(() => {
    if (weeklyCommissionPayment < 0) setDisabled(true);
    else setDisabled(false);
  }, [weeklyCommissionPayment]);

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-6">
        {hasSplit && (
          <Alert
            type="info"
            showIcon
            message="Commission split inferred"
            description="We could not find the program and success part split for this strategy, so the net commission was split evenly. Please adjust if needed."
            className="mb-4"
          />
        )}

        <Form.Item
          name={[name, "weekly_budget"]}
          label="Weekly Budget"
          rules={[
            { required: true, message: "Provide the weekly budget." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value >= changedMinWeeklyBudget) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(`Going below minimum possible weekly budget of ${formatMoney(changedMinWeeklyBudget)}`),
                );
              },
            }),
          ]}
        >
          <InputNumber
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            addonBefore="$"
            style={{ width: "100%" }}
            // min={getMinimumWeeklyBudget(
            //   initial_payment_timeline,
            //   initial_payment,
            //   weekly_budget,
            //   weeklyCommissionPayment,
            // )}
          />
        </Form.Item>

        {/* <Form.Item name={[name, "initial_payment"]} label="Initial Payment" rules={[{ required: true }]}>
          <InputNumber
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            addonBefore="$"
            disabled
            style={{ width: "100%" }}
            min={1}
          />
        </Form.Item> */}
        <div className="flex gap-2 items-center">
          <Form.Item
            className="flex-1"
            name={[name, "bias_amount"]}
            rules={[
              { required: false, message: "Provide the initial payment bias" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value < 0) {
                    return Promise.reject(new Error(`Initial payment bias cannot be negative.`));
                  }
                  if (!!value === false && initial_payment_charge === 0) {
                    return Promise.reject(new Error(`Both Initial payment charge % and Initial Bias can not be 0.`));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            label="Initial Payment"
          >
            <InputNumber style={{ width: "100%" }} addonBefore="$" />
          </Form.Item>
          <Form.Item
            className="flex-1"
            name={[name, "initial_payment_charge"]}
            rules={[
              { required: true, message: "Provide the initial payment charge %." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value < 0) {
                    return Promise.reject(new Error(`Initial payment charge % cannot be negative.`));
                  }
                  if (!!bias_amount === false && value === 0) {
                    return Promise.reject(new Error(`Both Initial payment charge % and Initial Bias can not be 0.`));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            label="Initial Percentage"
          >
            <InputNumber style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
          {/* <span className="bg-gray-100 px-3 py-1 rounded-xl">OR</span> */}
        </div>

        <Form.Item
          name={[name, "initial_payment_timeline"]}
          rules={[
            { required: true, message: "Provide the initial payment timeline (between 1-5 weeks)." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const max = 5;
                if (value >= 1 && value <= max) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(`Timeline cannot exceed 5 weeks or be below 1 week.`));
              },
            }),
          ]}
          label="Initial Payment Timeline"
        >
          <InputNumber style={{ width: "100%" }} addonAfter="weeks" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            name={[name, "program_part"]}
            label="Program Part (%)"
            rules={[
              { required: true, message: "Provide program fee %." },
              () => ({
                validator(_, value) {
                  if (value < 0) {
                    return Promise.reject(new Error("Program fee cannot be negative."));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>

          <Form.Item
            name={[name, "success_part"]}
            label="Success Part (%)"
            rules={[
              { required: true, message: "Provide success fee %." },
              () => ({
                validator(_, value) {
                  if (value < 0) {
                    return Promise.reject(new Error("Success fee cannot be negative."));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>

          <Form.Item
            className="col-span-2"
            name={[name, "net_commission"]}
            label="Net Commission [Program Part + Success Part] (%)"
          >
            <InputNumber disabled style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            name={[name, "commission_timeline"]}
            label="Commission Timeline"
            rules={[
              { required: true, message: "Provide the commission timeline in weeks." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const max = getFieldValue(["strategies", name, "weeks"]);
                  if (value >= 1 && value <= max) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(`Commission timeline cannot exceed ${max} or be below 1 week.`));
                },
              }),
            ]}
          >
            <InputNumber style={{ width: "100%" }} addonAfter="weeks" />
          </Form.Item>
          <Form.Item
            name={[name, "display_timeline"]}
            label="Display Timeline"
            rules={[
              { required: true, message: "Provide the display timeline in weeks." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const min = getFieldValue(["strategies", name, "commission_timeline"]);
                  const max = getFieldValue(["strategies", name, "weeks"]);
                  if (value >= min && value <= max) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(`Display timeline cannot exceed ${max} or be below ${min} week.`));
                },
              }),
            ]}
          >
            <InputNumber style={{ width: "100%" }} addonAfter="weeks" />
          </Form.Item>
        </div>

        {/* <Form.Item
          name={[name, "creditor_estimate"]}
          label={
            <div className="flex gap-4">
              <p>Creditor Estimate</p>
             
            </div>
          }
          rules={[{ required: true }]}
        >
          <InputNumber
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            addonBefore="$"
            style={{ width: "100%" }}
            min={0}
          />
        </Form.Item> */}

        <Form.Item label="Include commission payment to creditor amount?" name={[name, "include_commission_payment"]}>
          <Switch />
        </Form.Item>

        {/* <Form.Item>
          <Button htmlType="submit" className="w-full" type="primary" disabled={weeklyCommissionPayment < 0}>
            Save plan
          </Button>
        </Form.Item> */}
      </div>

      <div className="flex justify-center col-span-6">
        <Card title="Summary" className="shadow-md w-full mb-4">
          <Row gutter={[16, 16]} wrap>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: "#3f8600" }}
                title="Total Debt"
                value={totalDebt}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: "#3f8600" }}
                title="Estimated Weekly Budget"
                value={form.getFieldValue(["strategies", name, "weekly_budget"])}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: form.getFieldValue(["strategies", name, "weeks"]) < 0 ? "#cf1322" : "#3f8600" }}
                title="Weeks"
                value={
                  form.getFieldValue(["strategies", name, "weeks"]) < 0
                    ? "Weekly budget too low"
                    : form.getFieldValue(["strategies", name, "weeks"])
                }
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: "#3f8600" }}
                title="Initial Payment"
                value={weeklyInitialPayment}
                prefix="$"
                suffix={initial_payment_timeline === 1 ? "" : `(per ${initial_payment_timeline} weeks)`}
                precision={2}
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: "#3f8600" }}
                title="Net Commission"
                value={
                  calculateNetCommissionAmount(net_commission, totalDebt, initial_payment_charge) +
                  (initial_payment_charge / 100) * totalDebt
                }
                prefix="$"
                precision={2}
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{ color: weeklyCommissionPayment < 0 ? "#cf1322" : "#3f8600" }}
                title="Commission Payment"
                value={weeklyCommissionPayment < 0 ? "Exceeding weekly budget." : weeklyCommissionPayment}
                prefix={weeklyCommissionPayment < 0 ? "" : "$"}
                suffix={weeklyCommissionPayment < 0 ? "" : "/week"}
                precision={2}
              />
            </Col>
            <Col xs={24}>
              <Statistic
                valueStyle={{
                  color: form.getFieldValue(["strategies", name, "creditor_estimate"]) < 0 ? "#cf1322" : "#3f8600",
                }}
                title="Creditor Estimate"
                value={
                  form.getFieldValue(["strategies", name, "creditor_estimate"]) < 0
                    ? "Weekly budget too low"
                    : formatMoney(Number(form.getFieldValue(["strategies", name, "creditor_estimate"])))
                }
                // prefix={"$"}
                precision={2}
              />
            </Col>
            {/* <Col xs={24}>
              <Statistic
                valueStyle={{ color: "#3f8600" }}
                title="Creditor Estimate Split"
                value={creditor_estimate_split.length ? 0 : creditor_estimate_split[0]}
                prefix={"$"}
                precision={2}
              />
            </Col> */}
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default StrategyPanel;
